import graphql from "babel-plugin-relay/macro";
import { addDays, format } from "date-fns";
import { useAtomValue } from "jotai";
import { first, last } from "lodash";
import { useFragment } from "react-relay";

import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";

import { agendaQueryInputsAtom } from "../../atoms/agendaQueryInputs";
import { getAvailabilityTimeBounds } from "../../utils/getAvailabilityTimeBounds";
import { DateSelectorControlFragment_facility$key } from "./__generated__/DateSelectorControlFragment_facility.graphql";
import { DateSelectorControlInner } from "./components/DateSelectorControlInner";

type Props = {
  facilityRef: DateSelectorControlFragment_facility$key | null;
};
export const DateSelectorControl = ({ facilityRef }: Props) => {
  const agendaQueryInputs = useAtomValue(agendaQueryInputsAtom);
  const facility = useFragment(DateSelectorControlFragmentString, facilityRef);
  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    facility?.club ?? null
  );

  //  this default of 60 days matches what is done on the home page.
  const windowDays = facility?.publicBookingWindowDays ?? 60;

  const maxDuration = last(facility?.durations) ?? 240;
  const minDuration =
    agendaQueryInputs.duration ?? first(facility?.durations) ?? 15;

  const { startTime, endTime } = getAvailabilityTimeBounds(
    agendaQueryInputs.times,
    facility?.startEndTime?.startTime,
    facility?.startEndTime?.endTime
  );

  const startDate = format(new Date(), "yyyy-MM-dd");
  const endDate = format(addDays(new Date(), windowDays), "yyyy-MM-dd");
  if (!facility?.id) return null;

  return (
    <DateSelectorControlInner
      facilityId={facility.id}
      startDate={startDate}
      endDate={endDate}
      groupSize={agendaQueryInputs.groupSize}
      courseBucketIds={
        agendaQueryInputs.courseBucketIds?.length
          ? agendaQueryInputs.courseBucketIds
          : undefined
      }
      startTime={startTime}
      endTime={endTime}
      maxDuration={maxDuration}
      minDuration={minDuration}
      clubWeekStartsOn={clubWeekStartsOn}
    />
  );
};

export const DateSelectorControlFragmentString = graphql`
  fragment DateSelectorControlFragment_facility on Facility {
    startEndTime {
      startTime
      endTime
    }
    id
    publicBookingWindowDays
    durations
    club {
      ...useGetClubStartOfWeekNumberPatron_club
    }
  }
`;
